'use client';

/**
 * CurrencyInput — money field (Complete_app §17.1 "appropriate currency
 * inputs").
 *
 *     <FormField label="Contract value" name="value" optional>
 *       {(p) => <CurrencyInput {...p} currency={currency} value={amount} onChange={setAmount} />}
 *     </FormField>
 *
 *   where `const currency = useOrgCurrency();` (`@/lib/auth`) — the org's own
 *   currency. There is no desk-wide currency constant on the global desk.
 * - The symbol is a prefix the person can see; the currency is never assumed
 *   (null → no symbol, a bare number).
 * - Grouping is applied as they type (1,234,567), and amounts of 10,000 or
 *   more are read back in compact notation ("$12.5M") under the field, so a
 *   missing zero is visible.
 * - `onChange` receives a number or null (cleared). Decimals: up to 2.
 */
import type { InputHTMLAttributes } from 'react';
import { forwardRef, useEffect, useState } from 'react';

import {
  amountInWords,
  currencySymbol,
  groupAmount,
  normaliseAmountInput,
  parseAmount,
} from '@/lib/forms/money';

import { cn } from './cn';
import { Input } from './Input';

export interface CurrencyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'size' | 'prefix'> {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  currency: string | null | undefined;
  inputSize?: 'sm' | 'md';
  hideWords?: boolean;
}

function display(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return groupAmount(normaliseAmountInput(String(value)), currency);
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(function CurrencyInput(
  { value, onChange, currency, inputSize = 'md', hideWords = false, className, ...rest },
  ref,
) {
  const [text, setText] = useState(() => display(value, currency));

  // Follow outside changes (a reset, a restored draft) without fighting typing.
  useEffect(() => {
    if (parseAmount(text) !== (value ?? null)) setText(display(value, currency));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, currency]);

  const symbol = currencySymbol(currency);
  const words = hideWords ? null : amountInWords(value ?? null, currency);

  return (
    <div className={cn('space-y-1', className)}>
      <Input
        ref={ref}
        autoComplete="off"
        inputMode="decimal"
        inputSize={inputSize}
        prefix={symbol ?? undefined}
        type="text"
        value={text}
        onChange={(e) => {
          const normalised = normaliseAmountInput(e.target.value);
          setText(groupAmount(normalised, currency));
          onChange(parseAmount(normalised));
        }}
        {...rest}
      />
      {words ? (
        <p aria-live="polite" className="text-xs text-fg-muted">
          {words}
        </p>
      ) : null}
    </div>
  );
});
